import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageLoader, PageHeader, money, Stars } from "@/components/shared";
import { Disclaimer } from "@/components/Disclaimer";
import { ResaleSection } from "@/components/ResaleSection";
import { wishScore } from "@/lib/journey";
import { Home as HomeIcon, Check, X, HelpCircle, TrendingUp } from "lucide-react";

const VERDICT_LABEL = {
  love: { label: "Love it", cls: "bg-status-complete/15 text-status-complete" },
  maybe: { label: "Maybe", cls: "bg-status-warning/15 text-status-warning" },
  pass: { label: "Pass", cls: "bg-destructive/15 text-destructive" },
};

const isToured = (t) => {
  if (typeof t.toured === "boolean") return t.toured;
  const s = `${t.tour_date || ""} ${t.details?.Status || ""}`.toLowerCase();
  return s.includes("toured") || s.includes("shown") || s.includes("open house");
};

const d = (t, ...keys) => {
  for (const k of keys) { const v = t.details?.[k]; if (v) return v; }
  return "";
};

const parseMoney = (s) => {
  if (!s) return null;
  const m = String(s).match(/\$?\s*([\d,]+(?:\.\d+)?)/);
  return m ? parseFloat(m[1].replace(/,/g, "")) : null;
};

// Illustrative payment assumptions (clearly labeled in the UI — not a quote)
const DOWN_PCT = 0.10, RATE = 0.065, YEARS = 30;
const estMortgage = (price) => {
  const loan = price * (1 - DOWN_PCT);
  if (!loan) return 0;
  const r = RATE / 12, n = YEARS * 12;
  return (loan * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
};

const commuteOf = (t) => {
  const e = Object.entries(t.details || {}).find(([k]) => /commute|drive/i.test(k));
  return e ? e[1] : "";
};

function critStatus(t, crit) {
  const lc = crit.toLowerCase();
  const match = (arr) => (arr || []).find((x) => {
    const xl = String(x).toLowerCase();
    return xl.includes(lc) || lc.includes(xl);
  });
  const m = match(t.criteria_meets);
  if (m) return { label: "Fits", cls: "text-status-complete", Icon: Check, note: m };
  const n = match(t.criteria_not_meets);
  if (n) return { label: "Doesn't fit", cls: "text-destructive", Icon: X, note: n };
  return { label: "Need to confirm", cls: "text-status-warning", Icon: HelpCircle, note: "" };
}

export default function Compare() {
  const [resaleOpen, setResaleOpen] = useState(null);
  const wsQ = useQuery({ queryKey: ["workspace"], queryFn: async () => (await api.get("/buyer/workspace")).data });
  const toursQ = useQuery({ queryKey: ["buyer-tours"], queryFn: async () => (await api.get("/buyer/tours")).data });

  if (wsQ.isLoading || toursQ.isLoading) return <PageLoader />;

  const profile = wsQ.data.client.profile || {};
  const criteria = [...(profile.must_haves || []), ...(profile.nice_to_haves || [])];
  const toured = (toursQ.data || []).filter(isToured);

  const commuteKey = toured.map((t) => Object.keys(t.details || {}).find((k) => /commute|drive/i.test(k))).find(Boolean);
  const driveLabel = commuteKey || "Drive time";

  const totalMonthly = (t) => {
    const h = parseMoney(d(t, "HOA / maintenance fee", "HOA"));
    const tax = parseMoney(d(t, "Taxes", "Tax", "Property tax"));
    return h != null && tax != null ? `~$${Math.round(h + tax).toLocaleString()}/mo` : "";
  };

  const estTotalMonthly = (t) => {
    const price = t.price != null ? t.price : parseMoney(d(t, "List price"));
    if (!price) return "";
    const hoa = parseMoney(d(t, "HOA / maintenance fee", "HOA")) || 0;
    const tax = parseMoney(d(t, "Taxes", "Tax", "Property tax")) || 0;
    return `~$${Math.round(estMortgage(price) + hoa + tax).toLocaleString()}/mo`;
  };

  const rows = [
    ["Wish list match", (t) => { const s = wishScore(t, profile); return s ? `${s.pct}% (${s.met} of ${s.total})` : ""; }, "highlight"],
    ["Drive times (from Marlene)", (t) => (t.nearby?.drives || []).filter((x) => x.minutes).map((x) => `${x.label}: ~${x.minutes} min`).join(" · ")],
    ["Price", (t) => (t.price != null ? money(t.price) : d(t, "List price"))],
    ["HOA / maintenance fee", (t) => d(t, "HOA / maintenance fee", "HOA")],
    ["Property tax", (t) => d(t, "Taxes", "Tax", "Property tax")],
    ["HOA + tax / month", (t) => totalMonthly(t)],
    ["Est. total monthly payment", (t) => estTotalMonthly(t), "highlight"],
    ["Estimated resale range", (t) => t.resale?.est_resale_range || ""],
    ["Beds / baths", (t) => {
      const b = d(t, "Bedrooms"); const ba = d(t, "Baths");
      return b || ba ? `${b || "—"} bd / ${ba || "—"} ba` : "";
    }],
    ["Sq ft", (t) => d(t, "Living area", "Sq ft")],
    ["Parking", (t) => d(t, "Parking")],
    ["Lanai", (t) => d(t, "Lanai")],
    [driveLabel, (t) => commuteOf(t)],
    ["Year built / renovated", (t) => {
      const y = d(t, "Year built"); const r = d(t, "Renovated");
      return y ? (r ? `${y} · reno ${r}` : y) : (r ? `reno ${r}` : "");
    }],
  ];

  const blank = <span className="text-muted-foreground/50">—</span>;

  return (
    <div>
      <PageHeader title="Compare homes" subtitle="A side-by-side look at the homes you've toured, using what your agent has on file. Read-only — your agent keeps it up to date." />

      {toured.length === 0 ? (
        <Card className="p-12 rounded-2xl text-center" data-testid="compare-empty">
          <HomeIcon className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <p className="font-heading text-lg font-semibold">No toured homes yet</p>
          <p className="text-muted-foreground text-sm mt-1">Once you've toured homes with your agent, they'll line up here side by side.</p>
        </Card>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-border">
          <table className="w-full border-collapse text-sm" data-testid="compare-table">
            <thead>
              <tr>
                <th className="sticky left-0 z-10 bg-card p-3 text-left min-w-[150px] border-b border-border" />
                {toured.map((t, i) => (
                  <th key={t.id} className="p-3 text-left align-bottom min-w-[190px] border-l border-b border-border" data-testid={`compare-col-${i}`}>
                    <p className="font-heading font-semibold leading-tight">{t.title}</p>
                    {t.address && <p className="text-xs text-muted-foreground font-normal mt-0.5">{t.address}</p>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(([label, get, flag]) => {
                const hl = flag === "highlight";
                return (
                  <tr key={label} className="border-t border-border">
                    <td className={`sticky left-0 z-10 bg-card p-3 min-w-[150px] ${hl ? "font-semibold text-primary" : "font-medium text-muted-foreground"}`}>{label}</td>
                    {toured.map((t) => (
                      <td key={t.id} className={`p-3 border-l border-border align-top ${hl ? "bg-primary/5 font-semibold text-primary" : ""}`} data-testid={`compare-cell-${label.replace(/[^a-z]+/gi, "-").toLowerCase()}`}>
                        {get(t) || blank}
                      </td>
                    ))}
                  </tr>
                );
              })}

              {criteria.length > 0 && (
                <tr className="border-t border-border bg-secondary/50">
                  <td colSpan={toured.length + 1} className="p-2 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground sticky left-0">Your criteria</td>
                </tr>
              )}
              {criteria.map((crit) => (
                <tr key={crit} className="border-t border-border">
                  <td className="sticky left-0 z-10 bg-card p-3 text-muted-foreground min-w-[150px] align-top">{crit}</td>
                  {toured.map((t) => {
                    const s = critStatus(t, crit);
                    const Icon = s.Icon;
                    return (
                      <td key={t.id} className="p-3 border-l border-border align-top" data-testid={`compare-crit-cell`}>
                        <span className={`inline-flex items-center gap-1 font-medium ${s.cls}`}><Icon className="h-3.5 w-3.5 shrink-0" /> {s.label}</span>
                        {s.note && <p className="text-xs text-muted-foreground mt-0.5">{s.note}</p>}
                      </td>
                    );
                  })}
                </tr>
              ))}

              <tr className="border-t border-border bg-amber-100/40">
                <td colSpan={toured.length + 1} className="p-2 px-3 text-xs font-semibold uppercase tracking-wider text-amber-700 sticky left-0" data-testid="compare-your-ratings-heading">Your ratings</td>
              </tr>
              <tr className="border-t border-border">
                <td className="sticky left-0 z-10 bg-card p-3 font-medium text-muted-foreground min-w-[150px] align-top">Overall</td>
                {toured.map((t) => { const f = t.my_feedback || {}; return (
                  <td key={t.id} className="p-3 border-l border-border align-top" data-testid="compare-overall-cell">{f.overall ? <Stars value={f.overall} /> : blank}</td>
                ); })}
              </tr>
              <tr className="border-t border-border">
                <td className="sticky left-0 z-10 bg-card p-3 font-medium text-muted-foreground min-w-[150px] align-top">Verdict</td>
                {toured.map((t) => { const f = t.my_feedback || {}; const v = VERDICT_LABEL[f.verdict]; return (
                  <td key={t.id} className="p-3 border-l border-border align-top" data-testid="compare-verdict-cell">{v ? <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${v.cls}`}>{v.label}</span> : blank}</td>
                ); })}
              </tr>
              {criteria.map((crit) => (
                <tr key={`myrate-${crit}`} className="border-t border-border">
                  <td className="sticky left-0 z-10 bg-card p-3 text-muted-foreground min-w-[150px] align-top">{crit}</td>
                  {toured.map((t) => { const sc = (t.my_feedback?.criteria || {})[crit]; return (
                    <td key={t.id} className="p-3 border-l border-border align-top" data-testid="compare-myrating-cell">{sc ? <Stars value={sc} /> : blank}</td>
                  ); })}
                </tr>
              ))}

              <tr className="border-t border-border">
                <td className="sticky left-0 z-10 bg-card p-3 font-medium text-muted-foreground min-w-[150px] align-top">Resale &amp; recent sales</td>
                {toured.map((t) => (
                  <td key={t.id} className="p-3 border-l border-border align-top" data-testid="compare-resale-cell">
                    <Button variant="outline" size="sm" className="rounded-full h-8 gap-1.5" onClick={() => setResaleOpen(t)} data-testid={`compare-resale-view-${t.id}`}>
                      <TrendingUp className="h-3.5 w-3.5" /> View details
                    </Button>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!resaleOpen} onOpenChange={(v) => { if (!v) setResaleOpen(null); }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="compare-resale-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{resaleOpen?.title}</DialogTitle>
          </DialogHeader>
          {resaleOpen && <ResaleSection resale={resaleOpen.resale} showEmpty />}
        </DialogContent>
      </Dialog>

      {criteria.length === 0 && toured.length > 0 && (
        <Card className="p-4 rounded-2xl mt-4 bg-primary/5 border-primary/20">
          <p className="text-sm text-muted-foreground">Your criteria rows will appear here once your agent adds your must-haves to your profile.</p>
        </Card>
      )}

      <div className="mt-6">
        <Disclaimer>This table shows only saved property details and your own stated criteria. Look Before You Leap 808 does not rank homes, judge value, or call any home the "best." Blank cells mean the detail isn't on file yet. The estimated total monthly payment is a rough illustration only — it assumes 10% down, a 6.5% rate and a 30-year fixed loan, and is NOT a quote; confirm real numbers with your lender. Verify everything with your agent and licensed professionals. Source: HiCentral MLS / listing agent — deemed reliable but not guaranteed.</Disclaimer>
      </div>
    </div>
  );
}
